import React, {
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  useRef,
  forwardRef,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import api from 'api';
import DocumentsIcon from 'icons/Documents.icon';
import { DataRoomNode } from '../../models/DataRoomNode';
import DocumentStatusBadge from 'components/DocumentStatusBadge';
import { DataRoomMember } from '../../models/DataRoomMember';
import { FileAccessSelector } from 'components/FileAccessSelector';
import { Checkbox } from 'ncoded-component-library';
import {
  getArrayLength,
  getHasElementsInArray,
  getIsNodeFile,
  sortDataRoomNodes,
} from 'utils';
import { NodeChangedPermission } from 'components/FileAccessSelector/types';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import { LeadStagesCopies } from 'models/DealRoom';
import {
  confirmRemoveLeadsFromAutomation,
  confirmStopLeadsOrder,
} from 'modules/releaseOrderConfirm';
import { ReleaseOrderSharePreview } from '../../models/DataRoomBulk';

import './MemberFileAccessSelector.styles.scss';

export const PARENT_CLASS = 'member-file-access-selector';

const DEFAULT_ACCESS = {
  hasFullAccess: false,
  hasFullDownloadAccess: false,
};

export type MemberFileAccessSelectorProps = {
  className?: string;
  dataRoomId: string;
  dataRoomMemberId?: string;
  refetchFlag?: boolean;
  isDisabled?: boolean;
  defaultOpen?: boolean;
  showSelectAllCheckbox?: boolean;
  leadStagesCopies?: LeadStagesCopies;
  // With it, a lead's order files show their stage and the note shows under the list.
  releaseOrderNote?: React.ReactNode;
  updateMember?: (
    member: DataRoomMember,
    isRemovedFromAutomation?: boolean,
  ) => void;
  refetch?: () => void;
  onAutomationRemovalCancelled?: (
    leads: ReleaseOrderSharePreview['leads'],
  ) => void;
};

export type MemberFileAccessSelectorRef = {
  updateNodeIds: (dataRoomMember: DataRoomMember) => void;
};

const MemberFileAccessSelector: React.ForwardRefRenderFunction<
  MemberFileAccessSelectorRef,
  MemberFileAccessSelectorProps
> = (props, ref) => {
  const {
    className,
    dataRoomId,
    dataRoomMemberId,
    refetchFlag,
    isDisabled,
    defaultOpen = false,
    showSelectAllCheckbox = true,
    leadStagesCopies,
    releaseOrderNote,
    refetch,
    updateMember,
    onAutomationRemovalCancelled,
  } = props;
  const [checkedFilesIds, setCheckedFilesIds] = useState<string[]>([]);
  const [checkedDownloadIds, setCheckedDownloadIds] = useState<string[]>([]);
  const [hasBaseNodes, setHasBaseNodes] = useState(false);
  // Only the tree read sets it; share responses leave it out.
  const [isInReleaseOrder, setIsInReleaseOrder] = useState(false);
  const rootNodeIds = useRef<string[]>([]);

  const [access, setAccess] = useState(DEFAULT_ACCESS);

  const { hasFullAccess, hasFullDownloadAccess } = access || {};

  const classes = classNames(PARENT_CLASS, 'white-panel', className);

  const { t } = useTranslation();

  const { getCopies } = useLeadStagesCopy();

  const stageCopies = useMemo(
    () => getCopies(leadStagesCopies),
    [getCopies, leadStagesCopies],
  );

  const showReleaseOrderInfo = !!releaseOrderNote && isInReleaseOrder;

  // BE takes the lead out of automation when a share skips their order, so that needs a yes first.
  const shareCheckingOrder = useCallback(
    async (
      dataRoomNodeIds: string[],
      share: (isRemovingFromAutomation?: boolean) => Promise<void>,
    ) => {
      if (!isInReleaseOrder) return share();

      const {
        data: { leads, leadsCount, step },
      } = await api.dataRoom.nodes.previewReleaseOrderShare(dataRoomNodeIds, {
        changedPermissions: [{ dataRoomMemberId, enableView: true }],
      });

      if (!leadsCount) return share();

      const shouldShare = await confirmRemoveLeadsFromAutomation({
        leadNames: leads.map(({ name }) => name),
        leadsCount,
        filesCount: getArrayLength(dataRoomNodeIds),
        stage: step && stageCopies[step],
      });

      if (!shouldShare) return onAutomationRemovalCancelled?.(leads);

      await share(true);
      setIsInReleaseOrder(false);
    },
    [
      isInReleaseOrder,
      dataRoomMemberId,
      stageCopies,
      onAutomationRemovalCancelled,
    ],
  );

  // Taking away the file the lead's order waits on stops it there, so that needs a yes first.
  const revokeCheckingOrder = useCallback(
    async (dataRoomNodeIds: string[], revoke: () => Promise<void>) => {
      if (!isInReleaseOrder) return revoke();

      const { data } = await api.dataRoom.nodes.previewReleaseOrderShare(
        dataRoomNodeIds,
        { changedPermissions: [{ dataRoomMemberId, enableView: false }] },
      );

      if (await confirmStopLeadsOrder(data, stageCopies)) await revoke();
    },
    [isInReleaseOrder, dataRoomMemberId, stageCopies],
  );

  const isMixedAllViewAccess = useMemo(
    () => !hasFullAccess && getHasElementsInArray(checkedFilesIds),
    [checkedFilesIds, hasFullAccess],
  );
  const isMixedAllDownloadAccess = useMemo(
    () => !hasFullDownloadAccess && getHasElementsInArray(checkedDownloadIds),
    [checkedDownloadIds, hasFullDownloadAccess],
  );

  // Folders expand on the BE, so the root nodes cover the whole Data Room.
  const [onHasFullAccessChange, isLoadingHasFullAccess] = useCallbackAsync(
    async (hasFullAccess: boolean) => {
      const changeAccess = async (isRemovingFromAutomation?: boolean) => {
        const { data } = await api.dataRoom.nodes[
          hasFullAccess
            ? 'grantFilesAccessWithoutUndo'
            : 'revokeFilesAccessWithoutUndo'
        ](dataRoomMemberId);

        updateMember?.(data, isRemovingFromAutomation);
        refetch();
      };

      if (!hasFullAccess)
        return revokeCheckingOrder(rootNodeIds.current, changeAccess);

      await shareCheckingOrder(rootNodeIds.current, changeAccess);
    },
  );

  const [onHasFullDownloadAccessChange, isLoadingHasFullDownloadAccess] =
    useCallbackAsync(async (hasFullDownloadAccess: boolean) => {
      const changeAccess = async (isRemovingFromAutomation?: boolean) => {
        const { data } = await api.dataRoom.nodes[
          hasFullDownloadAccess
            ? 'grantDownloadFilesAccessWithoutUndo'
            : 'revokeDownloadFilesAccessWithoutUndo'
        ](dataRoomMemberId);

        updateMember?.(data, isRemovingFromAutomation);
        refetch();
      };

      if (!hasFullDownloadAccess) return changeAccess();

      await shareCheckingOrder(rootNodeIds.current, changeAccess);
    });

  const isLoading = isLoadingHasFullAccess || isLoadingHasFullDownloadAccess;

  const updateNodeIds = useCallback((dataRoomMember: DataRoomMember) => {
    const {
      uploadedFileIds,
      accessableDataRoomNodesView,
      accessableDataRoomNodesDownload,
      hasFullAccess,
      hasFullDownloadAccess,
    } = dataRoomMember;

    setCheckedFilesIds([...uploadedFileIds, ...accessableDataRoomNodesView]);
    setCheckedDownloadIds([
      ...uploadedFileIds,
      ...accessableDataRoomNodesDownload,
    ]);
    setAccess({ hasFullAccess, hasFullDownloadAccess });
  }, []);

  useImperativeHandle(ref, () => ({ updateNodeIds }), [updateNodeIds]);

  const getChildrenForNode = useCallback(
    async (id?: string) => {
      const {
        data: { dataRoomNodes, dataRoomMember },
      } = await api.dataRoom.nodes.getChildrenForMember(
        dataRoomId,
        id || null,
        dataRoomMemberId,
      );

      updateNodeIds(dataRoomMember);
      setIsInReleaseOrder(!!dataRoomMember.isInReleaseOrder);

      if (!id) {
        setHasBaseNodes(getHasElementsInArray(dataRoomNodes));
        rootNodeIds.current = dataRoomNodes.map(({ _id }) => _id);
      }

      return sortDataRoomNodes(dataRoomNodes);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataRoomId, dataRoomMemberId, refetchFlag],
  );

  const updateFileAccess = useCallback(
    async (newNode: NodeChangedPermission) => {
      const changeAccess = async (isRemovingFromAutomation?: boolean) => {
        const {
          data: { dataRoomMember },
        } = await api.dataRoom.members.updateMemberDocuments(dataRoomMemberId, {
          changedPermissions: [newNode],
        });

        updateNodeIds(dataRoomMember);
        updateMember?.(dataRoomMember, isRemovingFromAutomation);
      };

      if (!newNode.enable)
        return revokeCheckingOrder([newNode.dataRoomNodeId], changeAccess);

      await shareCheckingOrder([newNode.dataRoomNodeId], changeAccess);
    },
    [
      dataRoomMemberId,
      revokeCheckingOrder,
      shareCheckingOrder,
      updateMember,
      updateNodeIds,
    ],
  );

  const checkIfNodeIsDisabled = useCallback(
    (node: DataRoomNode) =>
      isDisabled ||
      isLoadingHasFullAccess ||
      isLoadingHasFullDownloadAccess ||
      (getIsNodeFile(node) && node.dataRoomOwner?._id === dataRoomMemberId),
    [
      isDisabled,
      isLoadingHasFullAccess,
      isLoadingHasFullDownloadAccess,
      dataRoomMemberId,
    ],
  );

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen={defaultOpen}
      trigger={
        <div className={`${PARENT_CLASS}__heading`}>
          <p className="heading">
            <DocumentsIcon />
            {t('dataRoom')}
          </p>
          {hasBaseNodes && (
            <DocumentStatusBadge
              numberOfCheckedFiles={checkedFilesIds.length}
            />
          )}
        </div>
      }
    >
      {hasBaseNodes && (
        <div className={`${PARENT_CLASS}__bulk-access`}>
          {showSelectAllCheckbox ? (
            <>
              <Checkbox
                disabled={isLoading}
                checked={hasFullAccess}
                className={classNames({ mixed: isMixedAllViewAccess })}
                label={t('view')}
                onChange={() => onHasFullAccessChange(!hasFullAccess)}
              />
              <Checkbox
                disabled={isLoading}
                checked={hasFullDownloadAccess}
                className={classNames({ mixed: isMixedAllDownloadAccess })}
                label={t('download')}
                onChange={() =>
                  onHasFullDownloadAccessChange(!hasFullDownloadAccess)
                }
              />
            </>
          ) : (
            <>
              <p>{t('view')}</p>
              <p>{t('download')}</p>
            </>
          )}
        </div>
      )}

      <FileAccessSelector
        hasLoading
        hasDownload
        refetchFlag={refetchFlag}
        checkedFilesIds={checkedFilesIds}
        checkedDownloadIds={checkedDownloadIds}
        getBaseNodes={getChildrenForNode}
        getChildrenForNode={getChildrenForNode}
        updateFileAccess={updateFileAccess}
        checkIfNodeIsDisabled={checkIfNodeIsDisabled}
        {...(showReleaseOrderInfo && { orderStageCopies: stageCopies })}
      />

      {showReleaseOrderInfo && releaseOrderNote}
    </CollapsibleSection>
  );
};

export default forwardRef(MemberFileAccessSelector);
